import { Box, Typography, Button, Card, Modal, Stack, Select, Option, FormControl, FormLabel, Textarea, Input, CircularProgress, Sheet, Divider, List, ListItem, ListItemContent, Alert, IconButton } from "@mui/joy";
import { useState, useEffect, useRef } from "react";
import { COLOR_BLACK, COLOR_PRIMARY } from "../utils/styles";
import { Add, Error, Edit, FileUpload } from "@mui/icons-material";
import apiService from "../utils/apiService";

function TastingNotesApp() {
  // State for data
  const [wines, setWines] = useState([]);
  const [tastingNotes, setTastingNotes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // State for modals
  const [openWineModal, setOpenWineModal] = useState(false);
  const [openTastingNoteModal, setOpenTastingNoteModal] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);

  // State for import
  const [importing, setImporting] = useState(false);
  const [importResults, setImportResults] = useState(null);
  const [showImportResults, setShowImportResults] = useState(false);
  const fileInputRef = useRef(null);

  // Generate vintage years for dropdown
  const currentYear = new Date().getFullYear();
  const vintageYears = ["NO VINTAGE", ...Array.from({ length: 200 }, (_, i) => currentYear - i)];

  // State for wine form
  const [wineFormData, setWineFormData] = useState({
    id: "",
    wineName: "",
    vintage: "NO VINTAGE",
    country: "",
    state: "",
    locale: "",
    subLocale: "",
    type: "",
    producer: "",
    varietal: "",
  });

  // State for tasting note form
  const [tastingNoteFormData, setTastingNoteFormData] = useState({
    id: "",
    wineId: "",
    tastingDate: new Date().toISOString().split('T')[0], // Today's date in YYYY-MM-DD
    defective: false,
    like: "yes",
    tastingNote: "",
  });

  // Fetch wines and tasting notes on component mount
  useEffect(() => {
    fetchWines();
    fetchTastingNotes();
  }, []);

  const fetchWines = async () => {
    setLoading(true);
    try {
      const data = await apiService.listWines();
      setWines(data);
    } catch (err) {
      setError('Failed to load wines. Please try again later.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchTastingNotes = async () => {
    setLoading(true);
    try {
      const data = await apiService.listTastingNotes();
      setTastingNotes(data);
    } catch (err) {
      setError('Failed to load tasting notes. Please try again later.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleAddWine = async () => {
    setLoading(true);
    try {
      if (isEditMode) {
        // Update existing wine
        await apiService.updateWine({
          ...wineFormData,
          wineId: wineFormData.id,
          // Convert vintage to number if it's not "NO VINTAGE"
          vintage: wineFormData.vintage === "NO VINTAGE" ? wineFormData.vintage : parseInt(wineFormData.vintage, 10),
        });
      } else {
        // Create new wine
        await apiService.createWine({
          ...wineFormData,
          // Convert vintage to number if it's not "NO VINTAGE"
          vintage: wineFormData.vintage === "NO VINTAGE" ? wineFormData.vintage : parseInt(wineFormData.vintage, 10),
        });
      }

      // Refresh wine list and close modal
      await fetchWines();
      setOpenWineModal(false);
      resetWineForm();
    } catch (err) {
      setError(err.message || 'Failed to save wine');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleAddTastingNote = async () => {
    setLoading(true);
    try {
      if (isEditMode) {
        // Update existing tasting note
        await apiService.updateTastingNote({
          ...tastingNoteFormData,
          noteId: tastingNoteFormData.id,
        });
      } else {
        // Create new tasting note
        await apiService.createTastingNote(tastingNoteFormData);
      }

      // Refresh tasting notes list and close modal
      await fetchTastingNotes();
      setOpenTastingNoteModal(false);
      resetTastingNoteForm();
    } catch (err) {
      setError(err.message || 'Failed to save tasting note');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleEditWine = (wine) => {
    setWineFormData({
      id: wine.id,
      wineName: wine.wineName || "",
      vintage: wine.vintage?.toString() || "NO VINTAGE",
      country: wine.country || "",
      state: wine.state || "",
      locale: wine.locale || "",
      subLocale: wine.subLocale || "",
      type: wine.type || "",
      producer: wine.producer || "",
      varietal: wine.varietal || "",
    });
    setIsEditMode(true);
    setOpenWineModal(true);
  };

  const handleEditTastingNote = (note) => {
    setTastingNoteFormData({
      id: note.id,
      wineId: note.wineId || "",
      tastingDate: note.tastingDate ? new Date(note.tastingDate).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
      defective: note.defective || false,
      like: note.like || "yes",
      tastingNote: note.tastingNote || "",
    });
    setIsEditMode(true);
    setOpenTastingNoteModal(true);
  };

  const resetWineForm = () => {
    setWineFormData({
      id: "",
      wineName: "",
      vintage: "NO VINTAGE",
      country: "",
      state: "",
      locale: "",
      subLocale: "",
      type: "",
      producer: "",
      varietal: "",
    });
    setIsEditMode(false);
  };

  const resetTastingNoteForm = () => {
    setTastingNoteFormData({
      id: "",
      wineId: "",
      tastingDate: new Date().toISOString().split('T')[0],
      defective: false,
      like: "yes",
      tastingNote: "",
    });
    setIsEditMode(false);
  };

  const handleWineFormChange = (e) => {
    const { name, value } = e.target;
    setWineFormData(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleTastingNoteFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    setTastingNoteFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  // Handler for file upload
  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Check if it's a CSV file
    if (!file.name.toLowerCase().endsWith('.csv')) {
      setError('Please upload a CSV file exported from CellarTracker');
      return;
    }

    setImporting(true);
    setError(null);

    try {
      // Use direct file upload instead of reading as text
      const results = await apiService.importTastingNotesFile(file);

      // Update state with results
      setImportResults(results);
      setShowImportResults(true);

      // Refresh data
      if (results.notesAdded > 0 || results.winesAdded > 0) {
        await Promise.all([fetchWines(), fetchTastingNotes()]);
      }
    } catch (err) {
      setError(err.message || 'Failed to import tasting notes');
      console.error(err);
    } finally {
      setImporting(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = null; // Reset the file input
      }
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        padding: "20px",
        height: "100%",
        overflow: "auto",
        alignItems: "center",
      }}
    >
      <Box
        sx={{
          maxWidth: "1200px",
          width: "100%",
        }}
      >
        <Typography level="h2" sx={{ mb: 3 }}>Wine Tasting Notes</Typography>

        {error && (
          <Alert
            color="danger"
            startDecorator={<Error />}
            sx={{ mb: 2 }}
            onClose={() => setError(null)}
          >
            {error}
          </Alert>
        )}

        {/* Action Buttons */}
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={2}
          sx={{ mb: 3 }}
          justifyContent="flex-end"
        >
          <Button
            variant="solid"
            color="neutral"
            sx={{
              color: "white",
              backgroundColor: COLOR_BLACK,
              "&:hover": {
                backgroundColor: COLOR_PRIMARY,
              },
            }}
            startDecorator={<Add />}
            onClick={() => {
              resetWineForm();
              setOpenWineModal(true);
            }}
          >
            Add a new wine
          </Button>
          <Button
            variant="solid"
            color="neutral"
            sx={{
              color: "white",
              backgroundColor: COLOR_BLACK,
              "&:hover": {
                backgroundColor: COLOR_PRIMARY,
              },
            }}
            startDecorator={<Add />}
            onClick={() => {
              resetTastingNoteForm();
              setOpenTastingNoteModal(true);
            }}
            disabled={wines.length === 0}
          >
            Add a tasting note
          </Button>
          <Button
            variant="outlined"
            color="neutral"
            startDecorator={importing ? <CircularProgress size="sm" /> : <FileUpload />}
            onClick={() => fileInputRef.current?.click()}
            disabled={importing}
          >
            {importing ? "Importing..." : "Import from CellarTracker"}
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv"
              style={{ display: 'none' }}
              onChange={handleFileUpload}
            />
          </Button>
        </Stack>

        {/* Import Results Modal */}
        <Modal
          open={showImportResults}
          onClose={() => setShowImportResults(false)}
        >
          <Sheet
            variant="outlined"
            sx={{
              maxWidth: 500,
              borderRadius: 'md',
              p: 3,
              boxShadow: 'lg',
              position: 'fixed',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
            }}
          >
            <Typography level="title-lg" sx={{ mb: 2 }}>
              Import Results
            </Typography>
            <Divider sx={{ my: 2 }} />

            {importResults && (
              <Stack spacing={2}>
                <Typography>Total rows processed: {importResults.totalRows}</Typography>
                <Typography>New wines added: {importResults.winesAdded}</Typography>
                <Typography>Tasting notes added: {importResults.notesAdded}</Typography>
                {importResults.notesFailed > 0 && (
                  <>
                    <Typography color="danger">Failed imports: {importResults.notesFailed}</Typography>
                    <Typography level="body-sm">
                      Some notes could not be imported. This could be due to formatting issues or missing required data.
                    </Typography>
                    {importResults.errors && importResults.errors.length > 0 && (
                      <Box
                        sx={{
                          mt: 1,
                          maxHeight: '200px',
                          overflow: 'auto',
                          bgcolor: 'background.level1',
                          p: 2,
                          borderRadius: 'sm',
                          fontSize: 'sm',
                        }}
                      >
                        <Typography level="body-sm" fontWeight="bold">Error details:</Typography>
                        {importResults.errors.slice(0, 5).map((err, idx) => (
                          <Typography key={idx} level="body-xs" sx={{ mt: 1 }}>
                            • {err.error}
                          </Typography>
                        ))}
                        {importResults.errors.length > 5 && (
                          <Typography level="body-xs" sx={{ mt: 1, fontStyle: 'italic' }}>
                            ... and {importResults.errors.length - 5} more errors
                          </Typography>
                        )}
                      </Box>
                    )}
                  </>
                )}
              </Stack>
            )}

            <Box sx={{ mt: 3, display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
              <Button
                variant="solid"
                color="primary"
                onClick={() => setShowImportResults(false)}
              >
                Close
              </Button>
            </Box>
          </Sheet>
        </Modal>

        {/* Content Grid */}
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2 }}>
          {/* Wines List */}
          <Card sx={{ flex: 1, overflow: 'auto', maxHeight: '600px', textAlign: 'left' }}>
            <Typography level="title-lg" sx={{ mb: 2 }}>Wines</Typography>
            {loading && wines.length === 0 ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                <CircularProgress />
              </Box>
            ) : wines.length === 0 ? (
              <Typography level="body-md" sx={{ textAlign: 'center', p: 3 }}>
                No wines added yet. Click &quot;Add a new wine&quot; to get started.
              </Typography>
            ) : (
              <List>
                {wines.map((wine) => (
                  <ListItem
                    key={wine.id}
                    sx={{
                      border: '1px solid',
                      borderColor: 'neutral.300',
                      borderRadius: 'sm',
                      mb: 1,
                    }}
                    endAction={
                      <IconButton
                        size="sm"
                        variant="plain"
                        color="primary"
                        onClick={() => handleEditWine(wine)}
                        aria-label="Edit wine"
                      >
                        <Edit />
                      </IconButton>
                    }
                  >
                    <ListItemContent>
                      <Typography level="title-sm">{wine.wineName}</Typography>
                      <Typography level="body-sm">
                        {wine.vintage} • {wine.producer}
                      </Typography>
                      <Typography level="body-xs">
                        {wine.varietal} • {wine.type} • {wine.country}, {wine.state}
                        {wine.locale ? `, ${wine.locale}` : ''}
                        {wine.subLocale ? `, ${wine.subLocale}` : ''}
                      </Typography>
                    </ListItemContent>
                  </ListItem>
                ))}
              </List>
            )}
          </Card>

          {/* Tasting Notes List */}
          <Card sx={{ flex: 1, overflow: 'auto', maxHeight: '600px', textAlign: 'left' }}>
            <Typography level="title-lg" sx={{ mb: 2 }}>My Tasting Notes</Typography>
            {loading && tastingNotes.length === 0 ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                <CircularProgress />
              </Box>
            ) : tastingNotes.length === 0 ? (
              <Typography level="body-md" sx={{ textAlign: 'center', p: 3 }}>
                No tasting notes yet. Select a wine and add your first tasting note.
              </Typography>
            ) : (
              <List>
                {tastingNotes.map((note) => {
                  const wine = wines.find(w => w.id === note.wineId) || {};
                  return (
                    <ListItem
                      key={note.id}
                      sx={{
                        border: '1px solid',
                        borderColor: 'neutral.300',
                        borderRadius: 'sm',
                        mb: 1,
                      }}
                      endAction={
                        <IconButton
                          size="sm"
                          variant="plain"
                          color="primary"
                          onClick={() => handleEditTastingNote(note)}
                          aria-label="Edit tasting note"
                        >
                          <Edit />
                        </IconButton>
                      }
                    >
                      <ListItemContent>
                        <Typography level="title-sm">{wine.wineName || 'Unknown Wine'}</Typography>
                        <Typography level="body-sm">
                          {new Date(note.tastingDate).toLocaleDateString()} • {note.like === 'yes' ? '👍' : note.like === 'no' ? '👎' : '😐'}
                          {note.defective ? ' • Defective' : ''}
                        </Typography>
                        <Typography level="body-xs">
                          {note.tastingNote}
                        </Typography>
                      </ListItemContent>
                    </ListItem>
                  );
                })}
              </List>
            )}
          </Card>
        </Box>

        {/* Wine Modal */}
        <Modal
          open={openWineModal}
          onClose={() => {
            setOpenWineModal(false);
            resetWineForm();
          }}
        >
          <Sheet
            variant="outlined"
            sx={{
              maxWidth: 500,
              borderRadius: 'md',
              p: 3,
              boxShadow: 'lg',
              position: 'fixed',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
            }}
          >
            <Typography level="title-lg" sx={{ mb: 2 }}>
              {isEditMode ? 'Edit Wine' : 'Add a New Wine'}
            </Typography>
            <Divider sx={{ my: 2 }} />

            <Stack spacing={2}>
              <FormControl required>
                <FormLabel>Wine Name</FormLabel>
                <Input
                  name="wineName"
                  value={wineFormData.wineName}
                  onChange={handleWineFormChange}
                  placeholder="e.g. Château Margaux"
                />
              </FormControl>

              <FormControl>
                <FormLabel>Vintage</FormLabel>
                <Select
                  name="vintage"
                  value={wineFormData.vintage}
                  onChange={(_, value) =>
                    setWineFormData(prev => ({ ...prev, vintage: value }))
                  }
                  placeholder="Select vintage"
                  disabled={isEditMode} // Disable in edit mode since vintage cannot be changed
                >
                  {vintageYears.map((year) => (
                    <Option key={year} value={year.toString()}>
                      {year}
                    </Option>
                  ))}
                </Select>
              </FormControl>

              <FormControl required>
                <FormLabel>Type</FormLabel>
                <Select
                  name="type"
                  value={wineFormData.type}
                  onChange={(_, value) =>
                    setWineFormData(prev => ({ ...prev, type: value }))
                  }
                  placeholder="Select wine type"
                >
                  <Option value="red">Red</Option>
                  <Option value="white">White</Option>
                  <Option value="rose">Rosé</Option>
                  <Option value="sparkling">Sparkling</Option>
                  <Option value="dessert">Dessert</Option>
                </Select>
              </FormControl>

              <FormControl>
                <FormLabel>Producer</FormLabel>
                <Input
                  name="producer"
                  value={wineFormData.producer}
                  onChange={handleWineFormChange}
                  placeholder="e.g. Château Margaux"
                />
              </FormControl>

              <FormControl>
                <FormLabel>Varietal</FormLabel>
                <Input
                  name="varietal"
                  value={wineFormData.varietal}
                  onChange={handleWineFormChange}
                  placeholder="e.g. Cabernet Sauvignon"
                />
              </FormControl>

              <FormControl>
                <FormLabel>Country</FormLabel>
                <Input
                  name="country"
                  value={wineFormData.country}
                  onChange={handleWineFormChange}
                  placeholder="e.g. France"
                />
              </FormControl>

              <FormControl>
                <FormLabel>State/Region</FormLabel>
                <Input
                  name="state"
                  value={wineFormData.state}
                  onChange={handleWineFormChange}
                  placeholder="e.g. Bordeaux"
                />
              </FormControl>

              <FormControl>
                <FormLabel>Locale</FormLabel>
                <Input
                  name="locale"
                  value={wineFormData.locale}
                  onChange={handleWineFormChange}
                  placeholder="e.g. Margaux"
                />
              </FormControl>

              <FormControl>
                <FormLabel>Sub-Locale</FormLabel>
                <Input
                  name="subLocale"
                  value={wineFormData.subLocale}
                  onChange={handleWineFormChange}
                  placeholder="e.g. Specific vineyard"
                />
              </FormControl>
            </Stack>

            <Box sx={{ mt: 3, display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
              <Button
                variant="plain"
                color="neutral"
                onClick={() => {
                  setOpenWineModal(false);
                  resetWineForm();
                }}
              >
                Cancel
              </Button>
              <Button
                variant="solid"
                color="primary"
                onClick={handleAddWine}
                loading={loading}
                disabled={!wineFormData.wineName}
              >
                {isEditMode ? 'Save Changes' : 'Add Wine'}
              </Button>
            </Box>
          </Sheet>
        </Modal>

        {/* Tasting Note Modal */}
        <Modal
          open={openTastingNoteModal}
          onClose={() => {
            setOpenTastingNoteModal(false);
            resetTastingNoteForm();
          }}
        >
          <Sheet
            variant="outlined"
            sx={{
              maxWidth: 500,
              borderRadius: 'md',
              p: 3,
              boxShadow: 'lg',
              position: 'fixed',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
            }}
          >
            <Typography level="title-lg" sx={{ mb: 2 }}>
              {isEditMode ? 'Edit Tasting Note' : 'Add a Tasting Note'}
            </Typography>
            <Divider sx={{ my: 2 }} />

            <Stack spacing={2}>
              <FormControl required>
                <FormLabel>Wine</FormLabel>
                <Select
                  name="wineId"
                  value={tastingNoteFormData.wineId}
                  onChange={(_, value) =>
                    setTastingNoteFormData(prev => ({ ...prev, wineId: value }))
                  }
                  placeholder="Select a wine"
                  disabled={isEditMode} // Don't allow changing the wine when editing
                >
                  {wines.map((wine) => (
                    <Option key={wine.id} value={wine.id}>
                      {wine.wineName} ({wine.vintage || 'N/A'}) - {wine.producer || 'Unknown producer'}
                    </Option>
                  ))}
                </Select>
              </FormControl>

              <FormControl required>
                <FormLabel>Tasting Date</FormLabel>
                <Input
                  name="tastingDate"
                  value={tastingNoteFormData.tastingDate}
                  onChange={handleTastingNoteFormChange}
                  type="date"
                />
              </FormControl>

              <FormControl>
                <FormLabel>Was this wine defective?</FormLabel>
                <Input
                  name="defective"
                  checked={tastingNoteFormData.defective}
                  onChange={handleTastingNoteFormChange}
                  type="checkbox"
                  slotProps={{
                    input: {
                      'aria-label': 'Wine was defective',
                    },
                  }}
                />
              </FormControl>

              <FormControl required>
                <FormLabel>Did you like this wine?</FormLabel>
                <Select
                  name="like"
                  value={tastingNoteFormData.like}
                  onChange={(_, value) =>
                    setTastingNoteFormData(prev => ({ ...prev, like: value }))
                  }
                >
                  <Option value="yes">Yes 👍</Option>
                  <Option value="neutral">Neutral 😐</Option>
                  <Option value="no">No 👎</Option>
                </Select>
              </FormControl>

              <FormControl>
                <FormLabel>Tasting Notes</FormLabel>
                <Textarea
                  name="tastingNote"
                  value={tastingNoteFormData.tastingNote}
                  onChange={handleTastingNoteFormChange}
                  minRows={3}
                  placeholder="Describe the wine's aroma, taste, finish, etc."
                />
              </FormControl>
            </Stack>

            <Box sx={{ mt: 3, display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
              <Button
                variant="plain"
                color="neutral"
                onClick={() => {
                  setOpenTastingNoteModal(false);
                  resetTastingNoteForm();
                }}
              >
                Cancel
              </Button>
              <Button
                variant="solid"
                color="primary"
                onClick={handleAddTastingNote}
                loading={loading}
                disabled={!tastingNoteFormData.wineId || !tastingNoteFormData.tastingDate}
              >
                {isEditMode ? 'Save Changes' : 'Add Tasting Note'}
              </Button>
            </Box>
          </Sheet>
        </Modal>
      </Box>
    </Box>
  );
}

export default TastingNotesApp;